import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, apiAction, apiFetch } from '../lib/api';

// Friendly copy for the ?sso_error=<reason> the Google callback redirects with.
const SSO_ERRORS: Record<string, string> = {
  domain: 'Only @moxo.com Google accounts can sign in.',
  state: 'Sign-in session expired — please try again.',
  exchange: 'Could not complete Google sign-in — please try again.',
  no_access: 'Your account isn’t provisioned yet. Ask an admin for access.',
  disabled: 'Google sign-in isn’t configured.',
};

// Google-only login: when SSO is configured (prod), the Google button is the
// ONLY way in — the magic-link form is retired (the backend refuses /request
// too). The email form renders solely as the dev/local fallback when Google
// creds aren't configured.
export default function Login() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // null = config still loading (render neither variant — avoids a flash of
  // the retired email form before we know Google is on).
  const [googleEnabled, setGoogleEnabled] = useState<boolean | null>(null);
  const [ssoError, setSsoError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ googleEnabled: boolean }>('/api/auth/config')
      .then((c) => setGoogleEnabled(!!c.googleEnabled))
      .catch(() => {
        // Config unreachable — fall back to the email form; if Google is
        // actually on, the backend gate answers with use_google_sso.
        setGoogleEnabled(false);
      });
    const reason = new URLSearchParams(window.location.search).get('sso_error');
    if (reason) setSsoError(SSO_ERRORS[reason] ?? 'Google sign-in failed — please try again.');
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiAction<{ ok: true; devLink?: string }>('/api/auth/request', { email });
      setSent(true);
      if (res.devLink) setDevLink(res.devLink);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          err.status === 403
            ? err.message.includes('use_google_sso')
              ? 'Email links are retired — use “Sign in with Google”.'
              : 'Only @moxo.com emails can sign in.'
            : err.message,
        );
      } else {
        setError('Something went wrong.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-shell">
      <div className="card login-card">
        <h1>Moxo Funnel</h1>
        <p className="subtle">Sign in with your @moxo.com Google account.</p>

        {ssoError && (
          <div className="error" style={{ marginTop: 12 }}>
            {ssoError}
          </div>
        )}

        {googleEnabled === null && (
          <p className="subtle" style={{ marginTop: 20 }}>
            Loading…
          </p>
        )}

        {googleEnabled === true && (
          <div style={{ marginTop: 20 }}>
            <a
              className="primary"
              href="/api/auth/google"
              style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}
            >
              Sign in with Google
            </a>
          </div>
        )}

        {googleEnabled === false &&
          (!sent ? (
            <form onSubmit={onSubmit} style={{ marginTop: 20 }}>
              <p className="subtle" style={{ fontSize: '0.85rem' }}>
                Google sign-in isn’t configured (local dev) — use an email link.
              </p>
              <input
                type="email"
                autoFocus
                required
                placeholder="you@moxo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
              />
              <div style={{ marginTop: 12 }}>
                <button className="primary" type="submit" disabled={submitting || !email}>
                  {submitting ? 'Sending…' : 'Send magic link'}
                </button>
              </div>
              {error && <div className="error">{error}</div>}
            </form>
          ) : (
            <div style={{ marginTop: 20 }}>
              <p>Check your inbox. The link expires in 15 minutes.</p>
              {devLink && (
                <>
                  <p className="subtle">Dev shortcut (SMTP not configured):</p>
                  <div className="dev-link">
                    <a href={devLink}>{devLink}</a>
                  </div>
                </>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}
